import { Building2Icon, UsersIcon } from 'lucide-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button, DataTable, EmptyState, ErrorNotice, PageHeading, Pagination, Panel, SearchInput, SelectInput, SkeletonTable } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { formatMoney } from '@/lib/cash-flow';
import {
  ACCOUNT_DEFAULT_SORT,
  ACCOUNT_SORT_FIELDS,
  ACCOUNT_SORT_KEYS,
  SALES_CONTACTS_PATH,
  listAccountOwners,
  listSalesAccounts,
  pageCount,
  placeOf,
} from '@/lib/sales-data';
import { formatDay, isNotAllowed } from '@/lib/sales-workspace';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';
import { AccountLink } from '@/components/sales-workspace/sales-records';

/** The owner filter's URL param. */
const OWNER_PARAM = 'owner';

/**
 * Every Salesforce account, not only those with events: who owns it, what is
 * open, and what it has booked. Search, owner, sort and page live in the URL.
 *
 * Dark: the API answers 403 to anyone off the sales workspace allow-list,
 * and the page then acts as an unknown address.
 *
 * @returns The page.
 */
export function SalesAccountsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const ownerId = params.get(OWNER_PARAM) ?? '';
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();
  const { sort, onSort } = useTableSort(ACCOUNT_SORT_KEYS, ACCOUNT_DEFAULT_SORT);

  const retry = (count: number, error: unknown): boolean => !isNotAllowed(error) && count < 2;
  const accounts = useQuery({
    queryKey: ['sales-accounts', debounced, ownerId, sort, page],
    queryFn: ({ signal }) => listSalesAccounts({ q: debounced, ownerId, page, sort }, signal),
    placeholderData: keepPreviousData,
    retry,
  });
  const owners = useQuery({ queryKey: ['sales-account-owners'], queryFn: ({ signal }) => listAccountOwners(signal), staleTime: 5 * 60_000, retry });

  if (accounts.isError && isNotAllowed(accounts.error)) return <Navigate to="/events" replace />;

  const setOwner = (next: string): void =>
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        if (next) nextParams.set(OWNER_PARAM, next);
        else nextParams.delete(OWNER_PARAM);
        nextParams.delete('page');
        return nextParams;
      },
      { replace: true },
    );

  const data = accounts.data;
  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        title="Accounts"
        subtitle="Every client and prospect in Salesforce: who owns it, what is open and what it has booked."
        action={
          <Button variant="secondary" to={SALES_CONTACTS_PATH}>
            <UsersIcon aria-hidden className="size-4" /> Contacts
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search accounts"
          placeholder="Account name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        <SelectInput density="compact" aria-label="Owner" value={ownerId} onChange={(event) => setOwner(event.target.value)} className="w-64">
          <option value="">Every owner</option>
          {(owners.data ?? []).map((owner) => (
            <option key={owner.ownerId} value={owner.ownerId}>
              {owner.ownerName || owner.ownerId} ({owner.accounts})
            </option>
          ))}
        </SelectInput>
        {data === undefined ? null : (
          <span className="text-portal-micro text-portal-muted">
            {data.total} {data.total === 1 ? 'account' : 'accounts'}
          </span>
        )}
      </div>

      {accounts.isError ? (
        <ErrorNotice title="Accounts unavailable" message={errorMessage(accounts.error)} />
      ) : accounts.isPending ? (
        <SkeletonTable label="Loading accounts…" />
      ) : accounts.data.rows.length === 0 ? (
        <Panel>
          {debounced || ownerId ? (
            <EmptyState message="No accounts match that search." />
          ) : (
            <EmptyState
              icon={<Building2Icon aria-hidden className="size-6" />}
              title="No accounts yet"
              message="Accounts sync in from Salesforce every few hours and appear here."
            />
          )}
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Account', sortKey: ACCOUNT_SORT_FIELDS.name },
            { label: 'Location', sortKey: ACCOUNT_SORT_FIELDS.location },
            { label: 'Owner', sortKey: ACCOUNT_SORT_FIELDS.owner },
            { label: 'Open deals', sortKey: ACCOUNT_SORT_FIELDS.openDeals },
            { label: 'Bookings', sortKey: ACCOUNT_SORT_FIELDS.bookings },
            { label: 'Booked value', sortKey: ACCOUNT_SORT_FIELDS.bookedValue },
            { label: 'Last booking', sortKey: ACCOUNT_SORT_FIELDS.lastBooked },
          ]}
          rightAlignedHeaders={[3, 4, 5]}
          sort={sort}
          onSort={onSort}
          minWidth={960}
        >
          {accounts.data.rows.map((account) => (
            <tr key={account.id} className="border-t border-portal-line align-middle">
              <td className="w-[30%] p-3 text-portal-small">
                <AccountLink id={account.id} name={account.name} className="font-bold text-brand hover:underline" />
                <span className="block text-portal-micro text-portal-muted">{[account.type, account.industry].filter(Boolean).join(' · ') || '—'}</span>
              </td>
              <td className="p-3 text-portal-small text-portal-muted">{placeOf(account.billingCity, account.billingState) || '—'}</td>
              <td className="p-3 text-portal-small text-portal-muted">{account.ownerName || '—'}</td>
              <td className="p-3 text-right text-portal-small whitespace-nowrap">{account.openDeals}</td>
              <td className="p-3 text-right text-portal-small whitespace-nowrap">{account.bookings}</td>
              <td className="p-3 text-right text-portal-small whitespace-nowrap">{account.bookedValue > 0 ? formatMoney(account.bookedValue) : '—'}</td>
              <td className="p-3 text-portal-small whitespace-nowrap">{formatDay(account.lastBookedDate, { withYear: true })}</td>
            </tr>
          ))}
        </DataTable>
      )}
      {data !== undefined && pageCount(data) > 1 ? (
        <Pagination
          page={page}
          totalPages={pageCount(data)}
          onChange={setPage}
          summary={`${(page - 1) * data.pageSize + 1}–${(page - 1) * data.pageSize + data.rows.length} of ${data.total}`}
        />
      ) : null}
    </section>
  );
}
